import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import { getUserRole, getUserDisplayName } from '../utils/roleUtils';
import ApprovalWorkflowSimulation from './ApprovalWorkflowSimulation';
import authenticatedFetch from '../utils/authenticatedFetch';

interface DashboardProps {
  user: any;
}

interface Restaurant {
  id: string;
  name: string;
  address: string;
  phone: string;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    revenueToday: 0,
    activeCustomers: 0,
    menuItems: 0,
  });
  const { getIdToken, session } = useAuth();

  const userRole = getUserRole(user, session);
  const userName = getUserDisplayName(user, session);
  const restaurantId = '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch enterprises (restaurants)
        const restaurantsResponse = await authenticatedFetch(API_ENDPOINTS.enterprises());

        if (restaurantsResponse.ok) {
          const responseData = await restaurantsResponse.json();
          // Handle both direct array and { data: [] } response formats
          const restaurantsData = Array.isArray(responseData) ? responseData : (responseData.data || []);
          setRestaurants(restaurantsData);
        }

        // Dashboard metrics - using default values until metrics microservice is implemented
        setMetrics({
          totalOrders: 0,
          revenueToday: 0,
          activeCustomers: 0,
          menuItems: 0,
        });

      } catch (err) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, getIdToken]);

  if (loading) {
    return (
      <div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Metrics Grid */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-value">{metrics.totalOrders.toLocaleString()}</div>
          <div className="metric-label">Total Orders</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">${metrics.revenueToday.toLocaleString()}</div>
          <div className="metric-label">Revenue Today</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{metrics.activeCustomers.toLocaleString()}</div>
          <div className="metric-label">Active Customers</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{metrics.menuItems.toLocaleString()}</div>
          <div className="metric-label">Menu Items</div>
        </div>
      </div>

      {/* Approval Workflow */}
      <ApprovalWorkflowSimulation user={user} />

      {/* Dashboard Cards */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>Welcome, {userName}</h3>
          <p>Role: <strong>{userRole === 'super_admin' ? 'Super Admin' : 
                            userRole === 'system_admin' ? 'System Admin' : 
                            userRole === 'enterprise_admin' ? 'Enterprise Admin' : 
                            userRole === 'manager' ? 'Manager' : 'Employee'}</strong></p>
          {restaurantId && <p>Restaurant: <strong>{restaurantId}</strong></p>}
          <p>You have access to the BigSystemOne management system.</p>
        </div>

        <div className="dashboard-card">
          <h3>Location Management</h3>
          <p>Manage location hierarchy, concepts, and operational settings.</p>
          <ul className="feature-list">
            <li>Enterprise and concept management</li>
            <li>Franchisee and organizational units</li>
            <li>Store operations and settings</li>
            <li>Hierarchy configuration</li>
          </ul>
          <Link to="/locations" className="btn-modern-accent">
            Manage Locations
          </Link>
        </div>

        {(userRole === 'super_admin' || userRole === 'system_admin') && (
          <div className="dashboard-card">
            <h3>User Management</h3>
            <p>Create and manage user accounts for your team.</p>
            <ul className="feature-list">
              <li>Add new employees</li>
              <li>Assign roles and permissions</li>
              <li>View user activity</li>
              <li>Access control</li>
            </ul>
            <Link to="/users" className="btn-modern-accent">
              Manage Users
            </Link>
          </div>
        )}

        <div className="dashboard-card">
          <h3>AI Features</h3>
          <p>Leverage AI-powered tools for your business.</p>
          <ul className="feature-list">
            <li>Voice ordering system</li>
            <li>Invoice processing</li>
            <li>Menu optimization</li>
            <li>Sales analytics</li>
          </ul>
          <Link to="/ai-tools" className="btn-modern-accent">
            Explore AI Tools
          </Link>
        </div>

        <div className="dashboard-card">
          <h3>Analytics & Reports</h3>
          <p>Monitor performance and business insights.</p>
          <ul className="feature-list">
            <li>Real-time sales data</li>
            <li>Customer insights</li>
            <li>Operational metrics</li>
            <li>Financial reports</li>
          </ul>
          <Link to="/analytics" className="btn-modern-accent">
            View Analytics
          </Link>
        </div>

        <div className="dashboard-card">
          <h3>Order Management</h3>
          <p>Track and manage incoming orders.</p>
          <ul className="feature-list">
            <li>Live order queue</li>
            <li>Order history</li>
            <li>Customer preferences</li>
            <li>Delivery tracking</li>
          </ul>
          <Link to="/orders" className="btn-modern-accent">
            View Orders
          </Link>
        </div>

        {restaurants.length > 0 && (
          <div className="dashboard-card">
            <h3>Restaurant Information</h3>
            {restaurants.map((restaurant) => (
              <div key={restaurant.id} style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{restaurant.name}</h4>
                <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#6c757d' }}>{restaurant.address}</p>
                <p style={{ margin: '0', fontSize: '14px', color: '#6c757d' }}>{restaurant.phone}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="dashboard-card" style={{ border: '1px solid #dc3545', backgroundColor: '#f8d7da' }}>
          <h3 style={{ color: '#721c24' }}>Error</h3>
          <p style={{ color: '#721c24', margin: 0 }}>{error}</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;